import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Gallery } from "@/components/common";

const STATES = ["under_review", "more_info", "declined", "inactive"];

function Approve({ a, onDone }) {
  const { t } = useI18n();
  const [f, setF] = useState({ password: "", service_areas: [a.city, a.service_area].filter(Boolean).join(", "), checks: "" });
  const go = async () => {
    try {
      await api.post(`/owner/applications/${a.id}/approve`, { password: f.password, services: a.services,
        service_areas: f.service_areas.split(",").map((x) => x.trim()).filter(Boolean), checks: f.checks.split(",").map((x) => x.trim()).filter(Boolean) });
      toast.success(t("o.approved_share", { email: a.email, pw: f.password })); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="approve-form">
      <Input placeholder={t("o.temp_pw6")} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="approve-password" />
      <Input placeholder={t("o.areas")} value={f.service_areas} onChange={(e) => setF({ ...f, service_areas: e.target.value })} data-testid="approve-areas" />
      <Input placeholder={t("o.checks")} value={f.checks} onChange={(e) => setF({ ...f, checks: e.target.value })} data-testid="approve-checks" />
      <Button className="w-full bg-teal-600 hover:bg-teal-700" onClick={go} data-testid="approve-confirm-btn">{t("o.approve_create")}</Button>
    </div>
  );
}

export default function OwnerApplications() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [approve, setApprove] = useState(null);
  const load = useCallback(() => api.get("/owner/applications").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (a, status) => {
    try { await api.post(`/owner/applications/${a.id}/status`, { status }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="owner-applications">
      {items.length === 0 && <p className="text-slate-400">{t("o.no_apps")}</p>}
      {items.map((a) => (
        <div key={a.id} className="rounded-2xl bg-white border border-slate-200 p-5 grid lg:grid-cols-[1.5fr_1fr] gap-5" data-testid={`application-${a.ref}`}>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold">{a.name}</p><span className="font-mono text-xs text-slate-400">{a.ref}</span>
              {a.kind === "maid" && <span className="text-xs rounded-full bg-blue-600 text-white px-2 py-0.5 font-semibold" data-testid="application-maid-badge">{t("mj.badge")}</span>}
              <span className="text-xs rounded-full bg-slate-100 px-2 py-0.5 font-semibold" data-testid="application-status">{t(a.status === "declined" ? "o.as_declined" : `o.a_${a.status}`)}</span>
            </div>
            <p className="text-sm text-slate-600 mt-1">{a.email} · {a.phone} · {a.city}{a.service_area && ` (${a.service_area})`}</p>
            <p className="text-sm mt-2">{a.kind === "maid" ? t("cl.label") : a.services.map((s) => t(`svc.${s}`)).join(" · ")}</p>
            <p className="text-xs text-slate-500 mt-1">{a.years_experience} {t("o.yrs")} · {a.languages} · {a.availability}</p>
            {a.references && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{a.references}</p>}
            <p className="text-[10px] font-mono text-slate-400 mt-2">{fmtDate(a.created_at)}</p>
          </div>
          <div>
            <Gallery files={a.files} />
            {a.status !== "approved" && (
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => setApprove(a)} data-testid={`approve-app-${a.ref}`}>{t("o.approve")}</Button>
                {STATES.map((s) => <Button key={s} size="sm" variant="outline" disabled={a.status === s} onClick={() => setStatus(a, s)} data-testid={`app-${s}-${a.ref}`}>{t(`o.a_${s}`)}</Button>)}
              </div>
            )}
          </div>
        </div>
      ))}
      <Dialog open={!!approve} onOpenChange={(o) => !o && setApprove(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("o.approve_n", { name: approve?.name || "" })}</DialogTitle></DialogHeader>
          {approve && <Approve a={approve} onDone={() => { setApprove(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
